import { currentPass } from '../networking/gate';

/**
 * Anonymous usage statistics, sent to the Worker's stats store (see worker/statsLogic.ts and the
 * admin dashboard at /admin). Nothing personal is sent: a random id for this browser install, a
 * random id for this visit, the game version, and what happened (opened the game, started or ended
 * a match and how, still playing, left the page). Development builds send nothing unless ?stats is in
 * the address.
 */

export type MatchMode = 'single' | 'skirmish' | 'multi' | 'load' | 'replay' | 'spectate';

export interface MatchInfo {
  mode: MatchMode;
  /** Multiplayer only: whether this browser hosted the match. */
  role?: 'host' | 'guest';
  players: number;
  humans: number;
  ais: number;
  map: string;
  size: string;
}

const enabled = typeof window !== 'undefined' && (!import.meta.env.DEV || new URLSearchParams(location.search).has('stats'));
const hex = (bytes: number) => [...crypto.getRandomValues(new Uint8Array(bytes))].map((b) => b.toString(16).padStart(2, '0')).join('');

function installId(): string {
  try {
    let id = localStorage.getItem('fc.uid');
    if (!id || !/^[0-9a-f]{16,32}$/.test(id)) {
      id = hex(12);
      localStorage.setItem('fc.uid', id);
    }
    return id;
  } catch {
    return hex(12);
  }
}

let sid = hex(8);
const uid = typeof window !== 'undefined' ? installId() : '';
let openedAt = 0;
let matchNo = 0;
let match: { info: MatchInfo; mid: number; at: number; clock: () => number } | null = null;

function send(event: Record<string, unknown>, beacon = false): void {
  if (!enabled) return;
  const pass = currentPass();
  if (!pass) return;
  const body = JSON.stringify({ ...event, sid, uid, v: __APP_VERSION__ });
  // Not '/stats': ad and tracker blockers commonly block addresses containing it.
  const url = `/api/pulse?p=${encodeURIComponent(pass)}`;
  // Leaving or hiding the page: a beacon still gets through while the page unloads or freezes.
  if (beacon && navigator.sendBeacon?.(url, new Blob([body], { type: 'text/plain' }))) return;
  void fetch(url, { method: 'POST', body, keepalive: true, headers: { 'content-type': 'application/json' } }).catch(() => {});
}

const visitSecs = () => (performance.now() - openedAt) / 1000;

/**
 * "Still here": the visit's length and, during a match, the match's length so far. Sent every
 * minute and whenever the page is hidden, so lengths are known even when the browser never says
 * goodbye (phones and killed tabs often don't).
 */
function beat(beacon = false): void {
  if (!openedAt) return;
  const m = match ? { mid: match.mid, msecs: (performance.now() - match.at) / 1000, gsecs: match.clock() } : {};
  send({ k: 'beat', secs: visitSecs(), ...m }, beacon);
}

/** The game has opened (after the server let it in). */
export function trackOpen(): void {
  if (openedAt) return;
  openedAt = performance.now();
  send({ k: 'open' });
  setInterval(() => {
    if (document.visibilityState === 'visible') beat();
  }, 60_000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') beat(true);
  });
  window.addEventListener('pagehide', () => {
    if (match) trackMatchEnd('quit', match.clock(), true);
    if (openedAt) send({ k: 'close', secs: visitSecs() }, true);
  });
  // Restored from the back/forward cache: a new visit.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    sid = hex(8);
    matchNo = 0;
    openedAt = performance.now();
    send({ k: 'open' });
  });
}

/** A match has started; clock reads its game clock in seconds. */
export function trackMatchStart(info: MatchInfo, clock: () => number): void {
  match = { info, mid: ++matchNo, at: performance.now(), clock };
  send({ k: 'start', mid: match.mid, ...info });
}

/** The match is over (won or lost) or was left; gameSecs is the game clock at that point. */
export function trackMatchEnd(result: 'win' | 'lose' | 'quit', gameSecs: number, beacon = false): void {
  if (!match) return;
  const { info, mid, at } = match;
  match = null;
  send({ k: 'end', mid, mode: info.mode, result, secs: (performance.now() - at) / 1000, gsecs: gameSecs }, beacon);
}
